import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import { Plus, Pencil, Trash2, Package, ImageIcon, X, AlertCircle, Gift, Crown, FolderOpen, Ban, Layers, Star, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Products() {
  const [establishment, setEstablishment] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    is_available: true,
    addons: [],
    free_addons_limit: 0,
    has_variations: false,
    variations: [],
    is_featured: false,
    is_on_sale: false,
    sale_price: ''
  });

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error:', err);
      setError('fetch_error');
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/categories`);
      setCategories(response.data);
    } catch (err) {
      console.error('Error:', err);
    }
  }, [establishment]);

  const fetchProducts = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/products`);
      setProducts(response.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [establishment]);

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      fetchCategories();
      fetchProducts();
    }
  }, [establishment, fetchCategories, fetchProducts]);

  const getSelectedCategory = () => {
    return categories.find(c => c.id === formData.category_id);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category_id: product.category_id,
        image_url: product.image_url,
        is_available: product.is_available,
        addons: product.addons || [],
        free_addons_limit: product.free_addons_limit || 0,
        has_variations: product.has_variations || false,
        variations: product.variations || [],
        is_featured: product.is_featured || false,
        is_on_sale: product.is_on_sale || false,
        sale_price: product.sale_price?.toString() || ''
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category_id: categories[0]?.id || '',
        image_url: '',
        is_available: true,
        addons: [],
        free_addons_limit: 0,
        has_variations: false,
        variations: [],
        is_featured: false,
        is_on_sale: false,
        sale_price: ''
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        free_addons_limit: parseInt(formData.free_addons_limit) || 0,
        addons: formData.addons.map(addon => ({
          name: addon.name,
          price: parseFloat(addon.price) || 0,
          is_required: addon.is_required || false,
          max_quantity: parseInt(addon.max_quantity) || 1,
          is_premium: addon.is_premium || false,
          is_available: addon.is_available !== false
        })),
        has_variations: formData.has_variations,
        variations: formData.variations.map(variation => ({
          name: variation.name,
          price: parseFloat(variation.price) || 0
        })),
        is_featured: formData.is_featured,
        is_on_sale: formData.is_on_sale,
        sale_price: formData.is_on_sale && formData.sale_price ? parseFloat(formData.sale_price) : null
      };

      if (editingProduct) {
        await axios.put(`${API}/establishments/${establishment.id}/products/${editingProduct.id}`, data);
        toast.success('Produto atualizado!');
      } else {
        await axios.post(`${API}/establishments/${establishment.id}/products`, data);
        toast.success('Produto criado!');
      }
      setShowDialog(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Deseja excluir este produto?')) return;
    try {
      await axios.delete(`${API}/establishments/${establishment.id}/products/${productId}`);
      toast.success('Produto excluído!');
      fetchProducts();
    } catch (err) {
      toast.error('Erro ao excluir produto');
    }
  };

  const addAddon = () => {
    setFormData(prev => ({
      ...prev,
      addons: [...prev.addons, { name: '', price: '0', is_required: false, max_quantity: 1, is_premium: false, is_available: true }]
    }));
  };

  const updateAddon = (index, field, value) => {
    setFormData(prev => {
      const addons = [...prev.addons];
      addons[index] = { ...addons[index], [field]: value };
      return { ...prev, addons };
    });
  };

  const removeAddon = (index) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== index)
    }));
  };

  // Variation management functions
  const addVariation = () => {
    setFormData(prev => ({
      ...prev,
      variations: [...prev.variations, { name: '', price: '' }]
    }));
  };

  const updateVariation = (index, field, value) => {
    setFormData(prev => {
      const variations = [...prev.variations];
      variations[index] = { ...variations[index], [field]: value };
      return { ...prev, variations };
    });
  };

  const removeVariation = (index) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Sem categoria';
  };

  const getCategory = (categoryId) => {
    return categories.find(c => c.id === categoryId);
  };

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  const selectedCategory = getSelectedCategory();

  return (
    <AdminLayout establishmentName={establishment?.name}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Produtos</h1>
          <p className="text-slate-500">Gerencie os produtos do seu cardápio</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-red-500 hover:bg-red-600" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhuma categoria</h3>
            <p className="text-slate-500 mb-4">Crie uma categoria primeiro para adicionar produtos</p>
            <Button onClick={() => window.location.href = '/admin/categories'} variant="outline">
              Ir para Categorias
            </Button>
          </CardContent>
        </Card>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhum produto</h3>
            <p className="text-slate-500 mb-4">Comece adicionando seu primeiro produto</p>
            <Button onClick={() => handleOpenDialog()} className="bg-red-500 hover:bg-red-600">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Produto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => {
            const category = getCategory(product.category_id);
            const categoryAddonsCount = category?.addons?.length || 0;
            const productAddonsCount = product.addons?.length || 0;
            const totalAddons = categoryAddonsCount + productAddonsCount;
            const hasVariations = product.has_variations && product.variations?.length > 0;
            const discountPercent = product.is_on_sale && product.sale_price 
              ? Math.round((1 - product.sale_price / product.price) * 100) 
              : 0;
            
            return (
              <Card key={product.id} className="overflow-hidden" data-testid={`product-card-${product.id}`}>
                <div className="h-40 bg-slate-100 relative">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <ImageIcon className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  {!product.is_available && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-medium">Indisponível</span>
                    </div>
                  )}
                  {/* Featured & Sale badges */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {product.is_featured && (
                      <span className="bg-amber-500 text-white px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1 shadow">
                        <Star className="w-3 h-3" />
                        Destaque
                      </span>
                    )}
                    {product.is_on_sale && (
                      <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1 shadow">
                        <Tag className="w-3 h-3" />
                        {discountPercent}% OFF
                      </span>
                    )}
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-800">{product.name}</h3>
                      <p className="text-xs text-slate-500">{getCategoryName(product.category_id)}</p>
                    </div>
                    <div className="text-right">
                      {hasVariations ? (
                        <p className="text-sm text-slate-500">
                          A partir de <span className="font-bold text-green-600">{formatCurrency(Math.min(...product.variations.map(v => v.price)))}</span>
                        </p>
                      ) : product.is_on_sale && product.sale_price ? (
                        <div>
                          <p className="text-xs text-slate-400 line-through">{formatCurrency(product.price)}</p>
                          <p className="font-bold text-red-500">{formatCurrency(product.sale_price)}</p>
                        </div>
                      ) : (
                        <p className="font-bold text-green-600">{formatCurrency(product.price)}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{product.description}</p>
                  <div className="flex flex-wrap gap-1 text-xs mb-3">
                    {hasVariations && (
                      <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {product.variations.length} variações
                      </span>
                    )}
                    {categoryAddonsCount > 0 && (
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                        <FolderOpen className="w-3 h-3" />
                        {categoryAddonsCount} da categoria
                      </span>
                    )}
                    {productAddonsCount > 0 && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                        {productAddonsCount} do produto
                      </span>
                    )}
                    {(product.free_addons_limit > 0 || category?.free_addons_limit > 0) && (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        {product.free_addons_limit || category?.free_addons_limit} grátis
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleOpenDialog(product)} data-testid={`edit-product-${product.id}`}>
                      <Pencil className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: X-Burger"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Preço</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                    required
                    data-testid="product-price-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="category">Categoria</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                        {cat.addons?.length > 0 && ` (${cat.addons.length} adicionais)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show inherited category addons */}
              {selectedCategory?.addons?.length > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FolderOpen className="w-4 h-4 text-blue-600" />
                    <Label className="text-blue-800 font-medium">Adicionais Herdados da Categoria</Label>
                  </div>
                  <p className="text-xs text-blue-700 mb-3">
                    Estes adicionais vêm da categoria "{selectedCategory.name}" e aparecerão automaticamente neste produto.
                  </p>
                  <div className="space-y-2">
                    {selectedCategory.addons.map((addon, index) => (
                      <div key={index} className={`flex items-center justify-between p-2 rounded text-sm ${addon.is_available === false ? 'bg-red-50' : 'bg-white'}`}>
                        <span className={addon.is_available === false ? 'text-red-600 line-through' : 'text-slate-700'}>{addon.name}</span>
                        <div className="flex items-center gap-2">
                          {addon.is_available === false && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded flex items-center gap-1">
                              <Ban className="w-3 h-3" />
                              Indisponível
                            </span>
                          )}
                          {addon.is_premium && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                              <Crown className="w-3 h-3" />
                              Premium
                            </span>
                          )}
                          <span className="text-slate-500">{formatCurrency(addon.price)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedCategory.free_addons_limit > 0 && (
                    <p className="text-xs text-blue-700 mt-2 flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      {selectedCategory.free_addons_limit} adicional(is) grátis definido na categoria
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o produto..."
                  data-testid="product-description-input"
                />
              </div>

              <div>
                <Label htmlFor="image">URL da Imagem</Label>
                <Input
                  id="image"
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                  data-testid="product-image-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_available}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_available: checked }))}
                  data-testid="product-available-switch"
                />
                <Label>Disponível para venda</Label>
              </div>

              {/* Featured & Promotion Section */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-slate-700 flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  Destaque e Promoção
                </h4>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                    data-testid="product-featured-switch"
                  />
                  <div>
                    <Label>Destacar na página inicial</Label>
                    <p className="text-xs text-slate-500">Produto aparecerá na seção de destaques</p>
                  </div>
                </div>

                <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_on_sale}
                      onCheckedChange={(checked) => setFormData(prev => ({ 
                        ...prev, 
                        is_on_sale: checked,
                        sale_price: checked ? prev.sale_price : ''
                      }))}
                      data-testid="product-sale-switch"
                    />
                    <div>
                      <Label className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-red-500" />
                        Ativar Promoção
                      </Label>
                      <p className="text-xs text-slate-500">Mostra badge de desconto e preço promocional</p>
                    </div>
                  </div>
                  
                  {formData.is_on_sale && (
                    <div className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label>Preço Promocional (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.sale_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, sale_price: e.target.value }))}
                          placeholder="0.00"
                          className="mt-1"
                          data-testid="product-sale-price-input"
                        />
                      </div>
                      {formData.price && formData.sale_price && (
                        <div className="pb-2 text-sm">
                          <span className="text-slate-500">Desconto: </span>
                          <span className="font-bold text-red-500">
                            {Math.round((1 - parseFloat(formData.sale_price) / parseFloat(formData.price)) * 100)}% OFF
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Variations Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.has_variations}
                      onCheckedChange={(checked) => setFormData(prev => ({ 
                        ...prev, 
                        has_variations: checked,
                        variations: checked ? prev.variations : []
                      }))}
                      data-testid="has-variations-switch"
                    />
                    <div>
                      <Label className="text-base flex items-center gap-2">
                        <Layers className="w-4 h-4 text-indigo-500" />
                        Habilitar Variações
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Ex: Tamanhos (P, M, G), Sabores, Cores
                      </p>
                    </div>
                  </div>
                </div>

                {formData.has_variations && (
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-indigo-800 font-medium">Variações do Produto</Label>
                      <Button type="button" size="sm" variant="outline" onClick={addVariation} data-testid="add-variation-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Variação
                      </Button>
                    </div>
                    <p className="text-xs text-indigo-700 mb-3">
                      Cada variação tem seu próprio preço. O cliente deverá escolher uma variação obrigatoriamente.
                    </p>
                    
                    {formData.variations.length === 0 ? (
                      <div className="text-center py-4 text-indigo-600 text-sm">
                        Adicione pelo menos uma variação para o produto
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {formData.variations.map((variation, index) => (
                          <div key={index} className="flex gap-2 items-center bg-white p-3 rounded-lg">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Nome (ex: Grande)"
                                value={variation.name}
                                onChange={(e) => updateVariation(index, 'name', e.target.value)}
                                data-testid={`variation-name-${index}`}
                              />
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Preço"
                                value={variation.price}
                                onChange={(e) => updateVariation(index, 'price', e.target.value)}
                                data-testid={`variation-price-${index}`}
                              />
                            </div>
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeVariation(index)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Product-specific Addons */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base">Adicionais Específicos do Produto</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Além dos adicionais da categoria, você pode adicionar itens específicos deste produto
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addAddon} data-testid="add-addon-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                {formData.addons.map((addon, index) => (
                  <div key={index} className={`mb-3 p-3 rounded-lg ${addon.is_available === false ? 'bg-red-50' : 'bg-slate-50'}`}>
                    <div className="flex gap-2 items-start mb-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Nome do adicional"
                          value={addon.name}
                          onChange={(e) => updateAddon(index, 'name', e.target.value)}
                          data-testid={`addon-name-${index}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Preço"
                          value={addon.price}
                          onChange={(e) => updateAddon(index, 'price', e.target.value)}
                          data-testid={`addon-price-${index}`}
                        />
                      </div>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeAddon(index)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={addon.is_premium || false}
                          onCheckedChange={(checked) => updateAddon(index, 'is_premium', checked)}
                          data-testid={`addon-premium-${index}`}
                        />
                        <Label className="text-sm flex items-center gap-1">
                          <Crown className="w-3 h-3 text-amber-500" />
                          Premium
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={addon.is_available !== false}
                          onCheckedChange={(checked) => updateAddon(index, 'is_available', checked)}
                          data-testid={`addon-available-${index}`}
                        />
                        <Label className={`text-sm flex items-center gap-1 ${addon.is_available === false ? 'text-red-600' : ''}`}>
                          {addon.is_available === false ? (
                            <>
                              <Ban className="w-3 h-3 text-red-500" />
                              Indisponível
                            </>
                          ) : (
                            'Disponível'
                          )}
                        </Label>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Free Addons Limit - only if there are product-specific addons */}
                {formData.addons.length > 0 && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="w-4 h-4 text-green-600" />
                      <Label className="text-green-800 font-medium">Limite de Adicionais Grátis (Produto)</Label>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      {selectedCategory?.free_addons_limit > 0 
                        ? `A categoria já define ${selectedCategory.free_addons_limit} grátis. Use aqui para sobrescrever ou deixe 0 para usar o da categoria.`
                        : 'Defina quantos adicionais não-premium o cliente pode escolher sem custo extra.'
                      }
                    </p>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="0"
                        value={formData.free_addons_limit}
                        onChange={(e) => setFormData(prev => ({ ...prev, free_addons_limit: e.target.value }))}
                        className="w-24"
                        data-testid="free-addons-limit-input"
                      />
                      <span className="text-sm text-green-700">
                        {formData.free_addons_limit > 0 
                          ? `${formData.free_addons_limit} adicional(is) grátis`
                          : selectedCategory?.free_addons_limit > 0
                            ? 'Usando limite da categoria'
                            : 'Todos cobrados'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="save-product-btn">
                {editingProduct ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
